import { ArrowLeft } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import Modal from "../../components/modal/Modal";
import images from "../../assets/images";
import { Training, Test, QuizQuestion } from "../../service/api";
import { useTrainingStore } from "../../store/store";
import QuizForm from "./QuizForm";
import axiosInstance from "../../common/axiosInstance";
import { useTrainingModule } from "./TrainingModuleContext";

interface CreateTestProps {
  mode: "create" | "edit" | "view";
}

const CreateTest: React.FC<CreateTestProps> = ({ mode }) => {
  const navigate = useNavigate();
  const { id: testId } = useParams<{ id?: string }>();
  const { moduleType, validCategories, paths } = useTrainingModule();
  const isFixedCategory = validCategories.length === 1;
  const [step, setStep] = useState(1);
  const [progress, setProgress] = useState(50);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [trainings, setTrainings] = useState<Training[]>([]);
  const location = useLocation(); // Now properly imported
  const searchParams = new URLSearchParams(location.search);
  const trainingIdFromQuery = searchParams.get("trainingId");
  const testCategoryFromQuery = searchParams.get("testCategory");

  const [test, setTest] = useState<Test>({
    id: testId || "",
    trainingId: trainingIdFromQuery || "",
    topic: "",
    testCategory:
      testCategoryFromQuery === "Pre-Test" ||
      testCategoryFromQuery === "Post-Test"
        ? testCategoryFromQuery
        : "Pre-Test",
    date: "",
    duration: "",
    quiz: [],
    status: "Draft",
    tenantId: "",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });

  const {
    trainings: fetchedTrainings,
    fetchTrainings,
    createTest,
    updateTest,
  } = useTrainingStore();

  useEffect(() => {
    const fetchTrainingData = async () => {
      try {
        const response = await fetchTrainings({
          type: "all",
          where: isFixedCategory
            ? { moduleType, category: validCategories[0] }
            : { moduleType },
        });
        if (response?.results) {
          const scopedResults = response.results.filter(
            (t: Training) =>
              t?.moduleType === moduleType ||
              (isFixedCategory && t?.category === validCategories[0]),
          );
          setTrainings(scopedResults);
          // Pre-select training if coming from table link
          if (trainingIdFromQuery && mode === "create") {
            const selectedTraining = response.results.find(
              (t: Training) => t.id === trainingIdFromQuery,
            ); // Added type annotation
            if (selectedTraining) {
              setTest((prev) => ({
                ...prev,
                trainingId: selectedTraining.id,
                topic: selectedTraining.topic,
                testCategory:
                  testCategoryFromQuery === "Pre-Test" ||
                  testCategoryFromQuery === "Post-Test"
                    ? testCategoryFromQuery
                    : new Date(selectedTraining.date) > new Date()
                      ? "Pre-Test"
                      : "Post-Test",
              }));
            }
          }
        }
      } catch (error) {
        console.error("Error fetching trainings:", error);
        setError("Failed to load training data");
      }
    };
    fetchTrainingData();
  }, [
    fetchTrainings,
    trainingIdFromQuery,
    testCategoryFromQuery,
    mode,
    moduleType,
    isFixedCategory,
  ]);

  const checkExistingTest = async (
    trainingId: string,
    testCategory: string,
  ) => {
    try {
      const response = await axiosInstance.get(
        `/api/test/training/${trainingId}`,
      );
      const existingTests = response.data.results || [];
      const existingTest = existingTests.find(
        (t: Test) => t.testCategory === testCategory,
      );
      return !!existingTest;
    } catch (error) {
      console.error("Error checking existing test:", error);
      return false;
    }
  };

  const handleNext = async () => {
    if (step === 1) {
      if (
        !test.trainingId ||
        !test.topic ||
        !test.testCategory ||
        !test.date ||
        !test.duration
      ) {
        setError("Please fill in all required fields");
        return;
      }
      const hasExistingTest = await checkExistingTest(
        test.trainingId,
        test.testCategory,
      );
      if (hasExistingTest) {
        setError(`${test.testCategory} already exists for this training.`);
        return;
      }
      setStep(2);
      setProgress(75);
      setError(null);
    }
  };

  const handlePrevious = () => {
    if (step > 1) {
      setStep(step - 1);
      setProgress(progress - 25);
      setError(null);
    }
  };

  const handleQuizUpdate = (quiz: QuizQuestion[]) => {
    if (mode === "view") return;
    setTest({ ...test, quiz });
  };

  const handleSubmit = async () => {
    if (mode === "view") return;

    setIsSubmitting(true);
    setError(null);

    try {
      if (
        !test.trainingId ||
        !test.topic ||
        !test.testCategory ||
        !test.date ||
        !test.duration ||
        !test.quiz ||
        test.quiz.length === 0
      ) {
        setStep(1);
        setProgress(50);
        throw new Error(
          "Please fill in all required fields and add at least one question",
        );
      }

      const testData: Test = {
        ...test,
        status: "Published",
        updatedAt: new Date().toISOString(),
      };

      let result;
      if (mode === "edit" && test.id) {
        result = await updateTest(test.id, testData);
      } else {
        const { id, ...testWithoutId } = testData;
        result = await createTest(testWithoutId);
      }

      setIsModalOpen(true);
    } catch (error) {
      console.error("Error submitting test:", error);
      setError(
        error instanceof Error ? error.message : "Failed to submit test",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalContinue = () => {
    setIsModalOpen(false);
    navigate(paths.dashboard);
  };

  return (
    <div className="px-2 py-4 mt-20">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-blue-800">
          {mode === "create" ? "Create" : mode === "edit" ? "Edit" : "View"}{" "}
          Test
        </h2>
        <Link to={paths.dashboard}>
          <button className="flex items-center gap-2 bg-[#61748e] text-white px-4 py-2 rounded-full">
            <ArrowLeft /> Back
          </button>
        </Link>
      </div>

      <div className="w-full bg-gray-200 rounded-full h-2.5 my-4">
        <div
          className="bg-[#0fa9a6] h-2.5 rounded-full"
          style={{ width: `${progress}%` }}
        ></div>
      </div>

      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
          <p>{error}</p>
        </div>
      )}

      {step === 1 && (
        <form
          className="relative bg-white p-8 mt-6 rounded-lg shadow-md"
          encType="multipart/form-data"
        >
          <h3 className="text-lg font-semibold text-gray-700 mb-6">
            Test Details
          </h3>
          <div className="grid grid-cols-4 gap-4">
            <div className="flex flex-col">
              <label className="text-gray-500 font-semibold mb-1">
                Topic *
              </label>
              <select
                className={`border-2 border-[#a5b7cf] p-3 rounded w-full ${
                  mode === "view" ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
                value={test.trainingId} // Use trainingId to store the ObjectId
                onChange={(e) => {
                  if (mode !== "view") {
                    const selectedTraining = trainings.find(
                      (t) => t.id === e.target.value,
                    );
                    if (selectedTraining) {
                      setTest({
                        ...test,
                        trainingId: selectedTraining.id, // Store the ObjectId
                        topic: selectedTraining.topic,
                        testCategory:
                          new Date(selectedTraining.date) > new Date()
                            ? "Pre-Test"
                            : "Post-Test",
                      });
                    }
                  }
                }}
                required
                disabled={mode === "view"}
              >
                <option value="">Select Topic</option>
                {trainings.map((training) => (
                  <option key={training.id} value={training.id}>
                    {training.topic}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col">
              <label className="text-gray-500 font-semibold mb-1">
                Test Category *
              </label>
              <select
                className={`border-2 border-[#a5b7cf] p-3 rounded w-full ${
                  mode === "view" ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
                value={test.testCategory}
                onChange={(e) =>
                  mode !== "view" &&
                  setTest({
                    ...test,
                    testCategory: e.target.value as "Pre-Test" | "Post-Test",
                  })
                }
                required
                disabled={mode === "view"}
              >
                <option value="Pre-Test">Pre-Test</option>
                <option value="Post-Test">Post-Test</option>
              </select>
            </div>
            <div className="flex flex-col">
              <label className="text-gray-500 font-semibold mb-1">
                Test Date *
              </label>
              <input
                type="date"
                className={`border-2 border-[#a5b7cf] p-3 rounded w-full ${
                  mode === "view" ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
                value={test.date}
                onChange={(e) =>
                  mode !== "view" && setTest({ ...test, date: e.target.value })
                }
                required
                disabled={mode === "view"}
              />
            </div>
            <div className="flex flex-col">
              <label className="text-gray-500 font-semibold mb-1">
                Test Duration (in mins) *
              </label>
              <input
                type="text"
                className={`border-2 border-[#a5b7cf] p-3 rounded w-full ${
                  mode === "view" ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
                value={test.duration}
                onChange={(e) =>
                  mode !== "view" &&
                  setTest({ ...test, duration: e.target.value })
                }
                required
                disabled={mode === "view"}
              />
            </div>
          </div>
        </form>
      )}

      {step === 2 && (
        <QuizForm
          onQuizUpdate={handleQuizUpdate}
          initialQuiz={
            test.quiz?.length > 0
              ? test.quiz
              : [
                  {
                    id: 1,
                    question: "",
                    options: [
                      { text: "", isCorrect: false },
                      { text: "", isCorrect: false },
                      { text: "", isCorrect: false },
                      { text: "", isCorrect: false },
                    ],
                  },
                ]
          }
          viewMode={mode === "view"}
        />
      )}

      <div className="flex bg-[#61748E] justify-end gap-4 px-2 py-2 mt-6">
        {step > 1 && (
          <button
            className="bg-[#869ab6] text-white px-6 py-2 rounded-full"
            onClick={handlePrevious}
            disabled={isSubmitting}
          >
            Previous
          </button>
        )}
        {step < 2 ? (
          <button
            className="bg-[#0fa9a6] text-white px-10 py-2 rounded-full"
            onClick={handleNext}
            disabled={isSubmitting}
          >
            Next
          </button>
        ) : mode !== "view" ? (
          <button
            className="bg-[#0fa9a6] text-white px-10 py-2 rounded-full disabled:opacity-50"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
        ) : null}
      </div>

      {isModalOpen && (
        <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
          <div className="bg-white w-[400px] rounded-xl flex flex-col justify-center items-center text-center h-full p-3">
            <img src={images.CheckMark} alt="Success" className="w-16" />
            <h4 className="text-xl font-semibold my-4">
              Successfully {mode === "edit" ? "updated" : "created"} Test
            </h4>
            <button
              className="px-3 bg-[#0fa9a6] text-white text-lg rounded-full py-1"
              onClick={handleModalContinue}
            >
              Continue
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default CreateTest;
